import React, { useContext, useEffect, useState } from 'react';
import { List, ListHeader, DataColumn, LinkColumn } from 'components/lists';
import { useFind } from 'hooks';
import { Context as AuthContext } from 'contexts/auth';
import { Context as CompanyContext } from 'contexts/company';
import { Config } from 'configs';
import { useNavigate } from 'react-router-dom';
import { PageTitle } from 'components/layouts/pageTitle';
import { PageContent } from 'components/layouts/pageContent';
import { ListLayout } from 'components/layouts/listLayout';
import { getTranslatedData } from 'locales';
import { NoRestaurantsBlock } from 'components/blocks/noRestaurants';
import { Pagination } from 'components/lists/pagination';

export const VariationJoins = () => {
  const { state: auth } = useContext(AuthContext);
  const { state: companyState, getCompanyRestaurants, deleteById, shouldDisplayNoRestaurantBlock } = useContext(CompanyContext);
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);

  const urlRootName = 'variation-joins';

  const requestData = {
    secretKey: auth.token,
    collectionName: Config.DATA.COLLECTIONS.VARIATION_JOINS,
    filter: `{ "company": ObjectId("${auth.company?.company}") }`,
    excludeCulture: true,
    loadOnInit: true,
    pageSize: Config.DEFAULTS.PAGE_SIZE,
    pageNumber: currentPage - 1,
    resourceV2: 'AdminVariationJoin',
    companyIdForV2: auth.company?.company,
  };

  const [find, list, isLoading, totalCount] = useFind(requestData);

  useEffect(() => {
    getCompanyRestaurants({ token: auth.token, companyId: auth.company?.company });
  }, []);

  const onEditClicked = (item) => {
    navigate(`/settings/${urlRootName}/${item._id}`);
  };

  const onDeleteClicked = async (item) => {
    await deleteById(
      {
        collectionName: Config.DATA.COLLECTIONS.VARIATION_JOINS,
        token: auth.token,
        id: item._id,
        resourceV2: 'AdminVariationJoin',
        companyIdForV2: auth.company?.company,
      },
      async () => {
        await find(requestData, true);
      },
    );
  };

  const renderVariationTypeName = (variationType) => {
    return Config.DATA.VARIATION_TYPES.find((vt) => vt.value === variationType)?.label || 'Unknown';
  };

  if (isLoading !== Config.DATA.LOADING_STATUS.INITIAL_LOAD && shouldDisplayNoRestaurantBlock()) {
    return <NoRestaurantsBlock />;
  }

  return (
    <ListLayout isLoading={isLoading === Config.DATA.LOADING_STATUS.INITIAL_LOAD || companyState.isLoadingRestaurants}>
      <PageTitle
        title="Variation joins"
        breadcrumbs={[{ path: '/settings', title: 'Settings' }]}
        isTopLevel
        hideMobile
      />
      <PageContent>
        <ListHeader
          title="Variation joins"
          description="General variation joins per company. Used to customize grouping of external products."
        >
          <button
            type="button"
            onClick={() => navigate(`/settings/${urlRootName}/add-new`)}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
          >
            Add new
          </button>
        </ListHeader>
        <List source={list} onRowClick={onEditClicked}>
          <DataColumn
            name="Template name"
            dbColumnName="template_name"
            headerClass="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
            dataCellClass="whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
          />
          <DataColumn
            name="Variation name"
            dbColumnName="name"
            render={(item) => getTranslatedData(item.name)}
            headerClass="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
            dataCellClass="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
          />
          <DataColumn
            name="Type"
            dbColumnName="variation_type"
            render={(item) => renderVariationTypeName(item.variation_type)}
            headerClass="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
            dataCellClass="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
          />
          <LinkColumn
            title="Delete"
            onClick={onDeleteClicked}
            askForConfirmation
            className="relative py-3.5 pl-3 sm:pr-6"
            dataCellClass="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6"
          />
          <Pagination
            pageSize={Config.DEFAULTS.PAGE_SIZE}
            totalCount={totalCount}
            currentPage={currentPage}
            onPageChanged={async (pageNumber) => {
              const request = { ...requestData, pageNumber: pageNumber - 1 };

              await find(request, false);
              setCurrentPage(pageNumber);
            }}
          />
        </List>
      </PageContent>
    </ListLayout>
  );
};
